'use client'

import { ThemeProvider } from "next-themes"
import ErrorBoundary from "./ErrorBoundry"
import { Suspense } from "react"
import Loading from "./Loading"
import { I18nextProvider } from "react-i18next"
import i18n from "../../../i18n"
import { AccountProvider } from "@/context/AccountContext"
import { BudgetProvider } from "@/context/BudgetContext"
import { BudgetManagerProvider } from "@/context/BudgetManagerContext"
import { ContactProvider } from "@/context/ContactContext"
import { FeedbackProvider } from "@/context/FeedbackContext"
import { InventoryProvider } from "@/context/InventoryContext"
import { InvoiceProvider } from "@/context/InvoiceContext"
import { PayrollProvider } from "@/context/PayrollContext"
import { ReferralProvider } from "@/context/ReferralContext"
import { TaxManagerProvider } from "@/context/TaxManagerContext"
import { TransactionProvider } from "@/context/TransactionContext"
import { ExportProvider } from "@/context/ExportDataContext"
import { ImportProvider } from "@/context/ImportContext"
import { CompanyProvider } from "@/context/CompanyContext"
import { BillProvider } from "@/context/BillContext"
import { BankLinkingProvider } from "@/context/BankLinkingContext"
import { CustomerProvider } from "@/context/CustomersContext"
import { TrackingProvider } from "@/context/TrackingContext"

/**
 * Client-side providers wrapper component
 * Organizes context providers in a hierarchical structure
 * Ensures proper data flow and dependency management
 */
const ClientProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <I18nextProvider i18n={i18n}>
          <BudgetManagerProvider>
            <CompanyProvider>
              {/* Primary business context providers */}
              <AccountProvider>
                <BudgetProvider>
                  {/* Team management context providers */}
                  {/* Transaction and inventory management */}
                  <TransactionProvider>
                    <TaxManagerProvider>
                      <InventoryProvider>
                        <InvoiceProvider>
                          {/* HR and customer management */}
                          <PayrollProvider>
                            <ReferralProvider>
                              <FeedbackProvider>
                                <ContactProvider>
                                  <ExportProvider>
                                    <ImportProvider>
                                      <BillProvider>
                                        <BankLinkingProvider>
                                          <CustomerProvider>
                                            <TrackingProvider>
                                              <Suspense fallback={<Loading />}>
                                                {children}
                                              </Suspense>
                                            </TrackingProvider>
                                          </CustomerProvider>
                                        </BankLinkingProvider>
                                      </BillProvider>
                                    </ImportProvider>
                                  </ExportProvider>
                                </ContactProvider>
                              </FeedbackProvider>
                            </ReferralProvider>
                          </PayrollProvider>
                        </InvoiceProvider>
                      </InventoryProvider>
                    </TaxManagerProvider>
                  </TransactionProvider>
                </BudgetProvider>
              </AccountProvider>
            </CompanyProvider>
          </BudgetManagerProvider>
        </I18nextProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default ClientProviders